'use strict';

/**
 * Exporta los datos visibles de una grilla AG-Grid a un archivo Excel (.xlsx).
 * Utiliza SheetJS (XLSX) desde window.XLSX (cargar xlsx.full.min.js antes de app.js).
 *
 * @param {Object} gridApi - API de la grilla AG-Grid (gridOptions.api)
 * @param {string} [fileName='datos_aggrid.xlsx'] - Nombre del archivo a descargar
 */
export function exportarAgGridAExcel(gridApi, fileName = 'datos_aggrid.xlsx') {
    const XLSX = typeof window !== 'undefined' && window.XLSX;
    if (!XLSX) {
        console.error('XLSX (SheetJS) no está cargado. Incluya el script xlsx.full.min.js en el HTML antes de app.js (ej. desde https://cdn.sheetjs.com/).');
        return;
    }
    if (!gridApi) {
        console.error("Error: gridApi no está disponible.");
        return;
    }
    const ESTILOS = {
        HEADER_BG: 'FFD3D3D3', // Gris claro para encabezados (ARGB Hex)
        HEADER_FONT: { bold: true },
        ROW_EVEN_BG: 'FFF0F0F0', // Gris muy claro para filas pares
    };

    function getValueByPath(obj, path) {
        if (obj == null || path == null || path === '') return undefined;
        const parts = String(path).split('.');
        let current = obj;
        for (const key of parts) {
            current = current?.[key];
        }
        return current;
    }

    const columnDefs = gridApi.columnController.getAllDisplayedColumns();
    const headers = columnDefs.map(col => col.colDef.headerName || col.getColId());

    const rowData = [];
    const anchosColumnas = [];

    columnDefs.forEach(col => {

        const widthPx = col.getActualWidth();
        anchosColumnas.push({ wch: Math.floor(widthPx / 7.5) });
    });

    gridApi.forEachNodeAfterFilterAndSort(node => {
        const rowValues = [];

        columnDefs.forEach(col => {
            const colId = col.getColId();
            const colDef = col.colDef;
            let value;
            const params = {
                    data: node.data,
                    node,
                    colDef,
                    getValue: (field) => getValueByPath(node.data, field),
                    api: gridApi,
                    column: col,
                    context: null,
                };

            if (typeof colDef.valueGetter === 'function') {
                try {
                    value = colDef.valueGetter(params);
                } catch (e) {
                    value = getValueByPath(node.data, colDef.field || colId);
                }
            } else {
                const field = colDef.field ?? colId;
                value = field && String(field).includes('.')
                    ? getValueByPath(node.data, field)
                    : (node.data && node.data[field]);
            }

            // --- Lógica de Limpieza / Conversión ---
            // Solo convertimos a float valores que son numéricos puros (no textos tipo "17 - Descripción", ni fechas "31/12/2027").
            if (value !== undefined && value !== null && value !== '') {
                let num = null;
                if (typeof value === 'number') {
                    num = Number.isNaN(value) ? null : value;
                } else if (typeof value === 'string') {
                    const trimmed = value.trim();
                    // Solo considerar número si la cadena es puramente numérica (dígitos, ., ,, espacios, - al inicio)
                    if (!/^-?[\d.,\s]+$/.test(trimmed)) {
                        num = null; // descartar: tiene letras, " - ", / (fechas), etc.
                    } else {
                        let cleaned = trimmed;
                        const lastComma = cleaned.lastIndexOf(',');
                        const lastDot = cleaned.lastIndexOf('.');
                        if (lastComma >= 0 && lastDot >= 0) {
                            // El último separador es el decimal: US "56,559,189.92" o ES "56.559.189,92"
                            if (lastComma > lastDot) {
                                cleaned = cleaned.replace(/\./g, '').replace(',', '.');
                            } else {
                                cleaned = cleaned.replace(/,/g, '');
                            }
                        } else if (cleaned.includes(',')) {
                            cleaned = cleaned.replace(',', '.');
                        }
                        num = parseFloat(cleaned);
                    }
                } else if (typeof value === 'object' && value && value.toISOString) {
                    params.value = value;
                    value.setSeconds(value.getSeconds() + 49); //Sumo 49 secs por error en excel
                }
                if (num !== null && !Number.isNaN(num)) {
                    value = parseFloat(num);
                }
            }
            // ---------------------------------------

            // Excel limita cada celda a 32767 caracteres; truncamos para evitar error al exportar
            const MAX_CARACTERES_EXCEL = 32767;
            if (typeof value === 'string' && value.length > MAX_CARACTERES_EXCEL) {
                value = value.substring(0, MAX_CARACTERES_EXCEL - 3) + '...';
            }

            rowValues.push((value == null || value == 'null') ? '' : value);
        });
        rowData.push(rowValues);
    });

    // 3. Crear el libro de Excel (Workbook)
    const workBook = XLSX.utils.book_new();

    // Usar array de arrays para preservar orden de columnas y encabezados duplicados (ej. Gerencia/Subgerencia/Departamento repetidos).
    // json_to_sheet con objetos usa las claves del objeto y columnas con el mismo headerName se pisaban.
    const aoa = [headers].concat(rowData);
    const workSheet = XLSX.utils.aoa_to_sheet(aoa);

    XLSX.utils.book_append_sheet(workBook, workSheet, "Datos");
    workSheet['!cols'] = anchosColumnas;
    const range = XLSX.utils.decode_range(workSheet['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ c: C, r: 0 });
        if (!workSheet[cellAddress]) continue;

        workSheet[cellAddress].s = {
            fill: { fgColor: { rgb: ESTILOS.HEADER_BG } },
            font: ESTILOS.HEADER_FONT,
        };
    }
    // 4. Descargar el archivo
    XLSX.writeFile(workBook, fileName);
    console.log(`Archivo ${fileName} generado con éxito.`);
}
